import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchCached,
  invalidateLmsCache,
  LMS_CACHE_KEYS,
} from '../services/lmsDataCache';
import {
  buildAdminBatchLedger,
  buildAdminCoursesOverview,
  buildAdminEnrollmentQueue,
  buildAdminPaymentsOverview,
  loadAdminWorkspaceRaw,
  loadInstructorBaseRaw,
  loadStudentProfileRaw,
  loadStudentWorkspaceRaw,
} from '../services/lmsDataLoaders';
import { buildStudentJourneyState } from '../utils/lmsSelectors';

const useCachedLoader = (cacheKey, loader, deps = [], options = {}) => {
  const { enabled = true, ttl } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const forceNextRef = useRef(false);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const refresh = useCallback(() => {
    if (cacheKey) {
      invalidateLmsCache(cacheKey);
    }
    forceNextRef.current = true;
    setRefreshToken((value) => value + 1);
  }, [cacheKey]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return undefined;
    }

    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const force = forceNextRef.current;
        forceNextRef.current = false;
        const result = cacheKey
          ? await fetchCached(cacheKey, () => loaderRef.current(), {
              ttl,
              force,
            })
          : await loaderRef.current();

        if (mounted) {
          setData(result);
        }
      } catch (err) {
        if (mounted) {
          setError(err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, refreshToken, cacheKey]);

  return {
    data,
    loading,
    error,
    refresh,
  };
};

const useAdminWorkspace = (enabled = true) =>
  useCachedLoader(LMS_CACHE_KEYS.adminWorkspace, loadAdminWorkspaceRaw, [], { enabled });

export const useCurrentStudentProfile = (userId) =>
  useCachedLoader(
    userId ? LMS_CACHE_KEYS.studentProfile(userId) : null,
    () => loadStudentProfileRaw(userId),
    [userId],
    { enabled: Boolean(userId) },
  );

/** @deprecated Prefer useStudentCourses — returns the same enrollments subset. */
export const useStudentEnrollments = (userId) => {
  const workspace = useStudentCourses(userId);
  return {
    ...workspace,
    data: workspace.data
      ? {
          enrollments: workspace.data.enrollments,
          enrollmentRequests: workspace.data.enrollmentRequests,
        }
      : null,
  };
};

export const useStudentPayments = (userId, options = {}) => {
  const { enabled = Boolean(userId) } = options;
  const workspace = useStudentCourses(userId, { enabled });

  return {
    data: workspace.data
      ? {
          payments: workspace.data.payments,
          paymentRequests: workspace.data.paymentRequests,
        }
      : null,
    loading: workspace.loading,
    error: workspace.error,
    refresh: workspace.refresh,
  };
};

export const useStudentCourses = (userId, options = {}) => {
  const { enabled = Boolean(userId) } = options;

  return useCachedLoader(
    userId ? LMS_CACHE_KEYS.studentWorkspace(userId) : null,
    async () => {
      if (!userId) {
        return {
          allCourses: [],
          activeCourses: [],
          courseMap: {},
          statusMap: {},
          enrollments: [],
          enrollmentRequests: [],
          paymentRequests: [],
          payments: [],
          courses: {},
        };
      }
      return loadStudentWorkspaceRaw(userId);
    },
    [userId],
    { enabled },
  );
};

export const useAdminCoursesOverview = (options = {}) => {
  const { enabled = true } = options;
  const workspace = useAdminWorkspace(enabled);

  return {
    data: workspace.data ? buildAdminCoursesOverview(workspace.data) : null,
    loading: workspace.loading,
    error: workspace.error,
    refresh: workspace.refresh,
  };
};

export const useAdminEnrollmentQueue = (options = {}) => {
  const { enabled = true } = options;
  const workspace = useAdminWorkspace(enabled);

  return {
    data: workspace.data ? buildAdminEnrollmentQueue(workspace.data) : null,
    loading: workspace.loading,
    error: workspace.error,
    refresh: workspace.refresh,
  };
};

export const useAdminPaymentsOverview = (options = {}) => {
  const { enabled = true } = options;
  const workspace = useAdminWorkspace(enabled);

  return {
    data: workspace.data ? buildAdminPaymentsOverview(workspace.data) : null,
    loading: workspace.loading,
    error: workspace.error,
    refresh: workspace.refresh,
  };
};

export const useAdminBatchLedger = (options = {}) => {
  const { enabled = true } = options;
  const workspace = useAdminWorkspace(enabled);

  return {
    data: workspace.data ? buildAdminBatchLedger(workspace.data) : null,
    loading: workspace.loading,
    error: workspace.error,
    refresh: workspace.refresh,
  };
};

export const useInstructorWorkspace = (instructorId, options = {}) => {
  const { enabled = Boolean(instructorId) } = options;

  return useCachedLoader(
    LMS_CACHE_KEYS.instructorBase,
    async () => {
      const { courses, enrollments } = await loadInstructorBaseRaw();
      const instructorCourses = Object.entries(courses)
        .map(([id, course]) => ({ id, ...course }))
        .filter(
          (course) =>
            course.instructorId === instructorId ||
            course.instructors?.includes?.(instructorId),
        );

      return {
        courses: instructorCourses,
        enrollments,
      };
    },
    [instructorId],
    { enabled },
  );
};

export const useStudentJourneyData = ({
  user,
  student,
  courseData,
  ready = true,
}) =>
  useCachedLoader(
    user?.uid ? `student:journey:${user.uid}` : null,
    async () => {
      if (!user?.uid || !courseData) return null;

      return buildStudentJourneyState({
        user,
        student,
        enrollments: courseData.enrollments || [],
        enrollmentRequests: courseData.enrollmentRequests || [],
        paymentRequests: courseData.paymentRequests || [],
        payments: courseData.payments || [],
        courses: courseData.courses || {},
      });
    },
    [
      user?.uid,
      user?.emailVerified,
      student?.personalDetails?.phone,
      student?.personalDetails?.address,
      student?.updatedAt,
      student?.achievements,
      courseData?.enrollments,
      courseData?.enrollmentRequests,
      courseData?.paymentRequests,
      courseData?.payments,
      courseData?.courses,
      ready,
    ],
    { enabled: Boolean(user?.uid && courseData && ready) },
  );

export { invalidateAdminCache, invalidateStudentCache } from '../services/lmsDataCache';
